import { Injectable } from "@angular/core";
import { ConcurrentRequestHandlingEnum } from "@modules/common/enums/concurrent-request-handling-enum";
import { ApiCallOptions } from "@modules/common/models/api-call-options";
import { ApiProxyOptions } from "@modules/common/models/api-proxy-options";
import { ApiCallService } from "@modules/common/services/api-call.service";
import { Observable, Subject, Subscription } from "rxjs";
import { finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ConcurrentRequestHandlingService {
    constructor(private _apiCallService: ApiCallService) { }

    private _observables: { [proxyName: string]: PendingApiCall } = {};

    public callApi<T>(
        proxyName: string,
        apiCallOptions: ApiCallOptions,
        apiProxyOptions?: ApiProxyOptions
    ): Observable<T> {
        apiProxyOptions = this.getDefaultApiProxyOptions(apiProxyOptions);
        apiCallOptions = this.getAdjustedApiCallOptions(apiCallOptions, apiProxyOptions);

        if (apiProxyOptions.concurrentRequestHandling == ConcurrentRequestHandlingEnum.AllowAll) {
            return this._apiCallService.callApi(apiCallOptions);
        }

        if (apiProxyOptions.concurrentRequestHandling == ConcurrentRequestHandlingEnum.FirstOnly) {
            const existingRequest = this._observables[proxyName];
            if (existingRequest) {
                return existingRequest.subjects[0];
            } 
        }

        if (apiProxyOptions.concurrentRequestHandling == ConcurrentRequestHandlingEnum.LastOnly) {
            const existingRequest = this._observables[proxyName];
            if (existingRequest) {
                existingRequest.subscription.unsubscribe();
            }
        }

        const subject = new Subject<T>();
        this._observables[proxyName] = {
            subscription: this.createSubscription(proxyName, apiCallOptions),
            subjects: [subject]
        };

        return subject.asObservable();
    }

    private createSubscription(proxyName: string, apiCallOptions: ApiCallOptions): Subscription {
        return this._apiCallService
            .callApi(apiCallOptions).pipe(
            finalize(() => this.clearObservablesList(proxyName)))
            .subscribe(x => this.handleApiCallCompleted(x, proxyName));
    }

    private handleApiCallCompleted<T>(data: T, proxyName: string): void {
        const existingRequest = this._observables[proxyName];
        if (existingRequest) {
            for (let subject of existingRequest.subjects) {
                subject.next(data);
            }
        }
        this.clearObservablesList(proxyName);
    }

    private clearObservablesList(proxyName: string): void {
        delete this._observables[proxyName];
    }

    private getDefaultApiProxyOptions(apiProxyOptions?: ApiProxyOptions): ApiProxyOptions {
        apiProxyOptions = Object.assign({}, apiProxyOptions || {});

        if (typeof apiProxyOptions.concurrentRequestHandling === 'undefined') {
            apiProxyOptions.concurrentRequestHandling = ConcurrentRequestHandlingEnum.FirstOnly;
        }

        if (typeof apiProxyOptions.useLoadingIndicator !== 'boolean') {
            apiProxyOptions.useLoadingIndicator = true;
        }

        if (typeof apiProxyOptions.includeEnvironmentAndCase !== 'boolean') {
            apiProxyOptions.includeEnvironmentAndCase = true;
        }

        return apiProxyOptions;
    }

    private getAdjustedApiCallOptions(apiCallOptions: ApiCallOptions, apiProxyOptions: ApiProxyOptions): ApiCallOptions {
        apiCallOptions = Object.assign({}, apiCallOptions);

        if (typeof apiCallOptions.useLoadingIndicator !== 'boolean') {
            apiCallOptions.useLoadingIndicator = apiProxyOptions.useLoadingIndicator;
        }

        if (typeof apiCallOptions.includeEnvironmentAndCase !== 'boolean') {
            apiCallOptions.includeEnvironmentAndCase = apiProxyOptions.includeEnvironmentAndCase;
        }

        return apiCallOptions;
    }
}

interface PendingApiCall {
    subscription: Subscription;
    subjects: Subject<any>[];
}
